import { z } from 'zod';

/**
 * Validation schemas for the auth flow. Mirror Angular Reactive Form
 * validation rules from the existing member portal so backend rejection
 * paths match.
 */

export const LoginSchema = z.object({
  /** Email or mobile — the method is auto-detected from the value. */
  identifier: z.string().min(1, 'Enter your email or mobile number'),
  password: z.string().min(1, 'Required'),
  remember: z.boolean().optional(),
});

export type LoginValues = z.infer<typeof LoginSchema>;

export const SignupSchema = z.object({
  email: z.string().min(1, 'Required').email('Enter a valid email').toLowerCase(),
  password: z.string().min(8, 'At least 8 characters'),
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  /** Selected community / tenant the user wants to join. */
  tenantId: z.string().uuid('Pick a community').optional(),
});

export type SignupValues = z.infer<typeof SignupSchema>;

/**
 * Forgot-password takes either an email or a mobile number, gated by the
 * tenant's `authConfig.allowed_identifiers` ('email' | 'mobile' | 'both').
 * `method` tells the server which OTP channel to use; the server-side
 * `SignupOTPService` then resolves the right template
 * (`authConfig.otp.templates.reset.email.template_id` vs `.sms.template_id`).
 *
 * Format validation is method-aware:
 *   - method = 'email'   → must match a standard email pattern.
 *   - method = 'mobile'  → must be an E.164 string built by `buildE164`
 *                          (the same shape Login.tsx sends).
 */
const validateIdentifier = (value: { method: 'email' | 'mobile'; identifier: string }, ctx: z.RefinementCtx) => {
  if (value.method === 'email') {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.identifier)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['identifier'], message: 'Enter a valid email' });
    }
  } else if (!/^\+\d{8,15}$/.test(value.identifier)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['identifier'], message: 'Enter a valid mobile number' });
  }
};

export const ForgotSchema = z
  .object({
    method: z.enum(['email', 'mobile']),
    identifier: z.string().min(1, 'Required'),
  })
  .superRefine(validateIdentifier);

export type ForgotValues = z.infer<typeof ForgotSchema>;

/**
 * Reset uses a 6-digit OTP that was sent by the `forgotPassword` call to
 * whichever channel the user picked. `method` + `identifier` are
 * propagated through the URL so the reset screen submits the same
 * channel context. Mirrors the Angular member-portal
 * `ResetPasswordComponent` password rules: 8-char min + upper + lower +
 * digit + special.
 */
export const ResetSchema = z
  .object({
    method: z.enum(['email', 'mobile']),
    identifier: z.string().min(1, 'Required'),
    otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
    password: z
      .string()
      .min(8, 'At least 8 characters')
      .regex(/[A-Z]/, 'Include an uppercase letter')
      .regex(/[a-z]/, 'Include a lowercase letter')
      .regex(/[0-9]/, 'Include a number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Include a special character'),
    confirm: z.string().min(1, 'Required'),
  })
  .superRefine((value, ctx) => {
    validateIdentifier(value, ctx);
    if (value.password !== value.confirm) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirm'], message: 'Passwords must match' });
    }
  });

export type ResetValues = z.infer<typeof ResetSchema>;
